import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { useFamilyPassBalances, usePassProducts } from '@/data/passes';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/parent/passes')({
  component: ParentPasses,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const userResp = await supabase.auth.getUser();
      const familyId = userResp.data.user?.app_metadata.family_id as string | undefined;
      return familyId ?? null;
    },
  });
}

function formatSqlDate(value: string | null) {
  if (!value) return 'No expiration';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function formatKind(value: string) {
  return value.replaceAll('_', ' ');
}

function formatRemainingUses(value: number | null) {
  return value === null ? 'Unlimited uses' : `${value} uses left`;
}

function ParentPasses() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const balances = useFamilyPassBalances(familyId);
  const passProducts = usePassProducts();

  const balanceList = balances.data ?? [];
  const productList = passProducts.data ?? [];

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Class cards and packs
        </p>
        <h1 className="mt-2">Passes</h1>
      </header>

      <section aria-labelledby="active-passes-heading" className="space-y-4">
        <h2 id="active-passes-heading" className="text-[var(--text-xl)]">
          Your active passes
        </h2>

        {balanceList.length === 0 ? (
          <Empty
            title="No active passes"
            description="Purchased class cards, intro passes, memberships, and lesson packs will appear here."
          />
        ) : (
          <ul className="grid gap-3">
            {balanceList.map((balance) => {
              const product = balance.pass_products;
              return (
                <li
                  key={balance.id}
                  className="surface flex flex-wrap items-center justify-between gap-4 p-4"
                >
                  <div>
                    <p className="font-medium">{product?.name ?? 'Pass'}</p>
                    <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      <Badge tone="neutral">{formatKind(product?.kind ?? 'pass')}</Badge> Valid
                      until {formatSqlDate(balance.valid_until)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
                      {formatRemainingUses(balance.remaining_uses)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="available-passes-heading" className="space-y-4">
        <h2 id="available-passes-heading" className="text-[var(--text-xl)]">
          Available passes
        </h2>

        {productList.length === 0 ? (
          <p className="text-[var(--color-ink-muted)]">No passes are currently available.</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {productList.map((product) => (
              <li key={product.id} className="surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {formatKind(product.kind)}
                    </p>
                  </div>
                  <Badge tone="success">{formatCents(product.price_cents)}</Badge>
                </div>
                <p className="mt-3 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {product.included_uses ? `${product.included_uses} uses` : 'Unlimited uses'}
                  {product.valid_days ? ` · valid ${product.valid_days} days` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
